import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import type { SelectHTMLAttributes } from 'react';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  className?: string;
  baseClassName?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
}

function Select({ label, error, className = '', baseClassName = '', options, ...props }: SelectProps) {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  const selectClasses = `${baseClassName} ${className} w-full appearance-none pr-10`;
  const defaultValue = props.value !== undefined ? props.value : '';

  useEffect(() => {
    const selectElement = selectRef.current;

    if (selectElement) {
      const handleOpen = () => setIsSelectOpen(true);
      const handleClose = () => setIsSelectOpen(false);

      selectElement.addEventListener('focus', handleOpen);
      selectElement.addEventListener('blur', handleClose);

      return () => {
        selectElement.removeEventListener('focus', handleOpen);
        selectElement.removeEventListener('blur', handleClose);
      };
    }
  }, []);

  return (
    <div className='flex flex-col gap-1'>
      {label && (
        <label className='text-sm font-medium text-gray-700'>
          {label}
          {props.required && <span className='ml-1'>(required)*</span>}
        </label>
      )}

      <div className='relative'>
        <select ref={selectRef} className={selectClasses} defaultValue={defaultValue} {...props}>
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={option.disabled ? 'text-gray-400' : ''}
            >
              {option.label}
            </option>
          ))}
        </select>
        <div className='absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none'>
          <ChevronDown
            size={20}
            className={`text-gray-700 transition-transform duration-200 ${isSelectOpen ? 'transform rotate-180' : ''}`}
          />
        </div>
      </div>

      {error && <p className='text-sm text-red-600'>{error}</p>}
    </div>
  );
}

export default Select;
